import React from 'react';
import { Route, IndexRoute } from 'react-router';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';

import {
    SIGN_IN_METHOD_FACEBOOK_LOGIN,
    SIGN_IN_METHOD_APPLE_LOGIN,
    SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE,
} from '@eb/social-login-facebook';

import {
    checkAccountStatus,
    checkEmailExists,
    setUpForm,
    setUpSocialForm,
    checkPasswordReset,
} from '../common/utils';
import { startSIWATimer } from '../common/utils/siwaTimer';
import { setEmail, forceStld as forceStldAction } from '../common/actions/auth';
import {
    socialLogInUser,
    socialLogInSoftUser,
    socialLogFacebookUser,
} from '../common/actions/social';

import {
    CHECKOUT_PATH,
    RESET_PASSWORD_STATUS_PATH,
    SOFT_ACCOUNT_STATUS_PATH,
    LOG_IN_PATH,
    SIGN_UP_PATH,
    SOCIAL_SIGN_UP_PATH,
    SOCIAL_LOOK_UP_PATH,
    SOCIAL_LOG_IN_PATH,
    SOCIAL_LINK_LOGIN_PATH,
    LINK_ACCOUNTS_LOG_IN_PATH,
    CONFIRMATION_PATH,
    BASE_URL,
    RESET_PASSWORD_PATH,
    SET_PASSWORD_PATH,
    SET_LINKED_SOFT_ACCOUNTS_PATH,
    STATUS_PATH,
    LINK_SOCIAL_ACCOUNT_PATH,
} from '../common/constants';

import Page from './Page';
import ConnectedSignInScreen from '../common/containers/signin/ConnectedSignInScreen';
import ConnectedStatusPage from '../common/containers/status/ConnectedStatusPage';
import ConnectedSetPasswordPage from '../common/containers/setPassword/ConnectedSetPasswordPage';
import ConnectedConfirmationPage from '../common/containers/confirmation';
import ConnectedSocialLookupPage from '../common/containers/socialLookup';
import ConnectedCheckoutSignInScreen from '../common/containers/checkoutSignInLite/ConnectedCheckoutSignInScreen';
import ConnectedSocialLinkedAccountPage from '../common/containers/socialLinkedAccount';

import ConnectedSocialLoginPage from '../common/containers/socialLogin/ConnectedSocialLoginPage';

const getRoutes = (pageInfo, store) => {
    const PageWithInfo = (props) => <Page {...pageInfo} {...props} />;

    const _checkAccountStatus = (nextRouterState, replace) => {
        checkAccountStatus(store, replace);
    };

    const setUpSocialSingUpFormOnEnter = (nextRouterState, replace) => {
        setUpSocialForm(store, replace);
    };

    const setUpFormOnEnter = async (
        { location: { query, pathname } },
        replace,
    ) => {
        const {
            social: { socialData: apple },
        } = store.getState();
        const {
            isUser,
            accessToken,
            externalIdentities: { sign_in_methods, can_login } = {},
            email,
        } = apple;

        if (query.email) {
            store.dispatch(setEmail(query.email));
        }

        if (isUser) {
            if (isEmpty(sign_in_methods)) {
                /* Soft accounts can't login directly because they don't have a
                 * password or any other sign in method.
                 * When following SIWA for soft accounts, and there are no sign in methods
                 * we ask them to create a password first.
                 * TODO:
                 * Metrics?
                 */
                store.dispatch(socialLogInSoftUser());

                startSIWATimer(store);

                return replace(`${BASE_URL}/${SET_LINKED_SOFT_ACCOUNTS_PATH}`);
            } else if (
                sign_in_methods &&
                sign_in_methods.includes(SIGN_IN_METHOD_APPLE_LOGIN)
            ) {
                await store.dispatch(
                    socialLogInUser(SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE, {
                        accessToken,
                        sourceProvider: SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE,
                        skipCreation: false,
                        email,
                    }),
                );
            } else if (
                sign_in_methods &&
                isEqual(sign_in_methods, [SIGN_IN_METHOD_FACEBOOK_LOGIN])
            ) {
                return store.dispatch(socialLogFacebookUser());
            }

            startSIWATimer(store);
            store.dispatch(forceStldAction());
            replace(`${BASE_URL}/${SOCIAL_LINK_LOGIN_PATH}`);
        } else if (accessToken) {
            startSIWATimer(store);
            store.dispatch(forceStldAction());

            replace(`${BASE_URL}/${SOCIAL_LOOK_UP_PATH}`);
        }

        setUpForm(store);
    };

    const checkSocialToken = (nextRouterState, replace) => {
        const {
            social: {
                socialData: { apple: { accessToken } = {} },
            },
        } = store.getState();

        if (!accessToken) {
            replace(BASE_URL);
        }
    };

    const setUpSocialLinkFormOnEnter = ({ location: { query } }, replace) => {
        const {
            social: {
                socialData: { apple: { email } = {} },
            },
        } = store.getState();

        if (email) {
            store.dispatch(setEmail(email));
        } else {
            replace(BASE_URL);
        }

        setUpForm(store);
    };

    const checkEmailOnEnter = (nextRouterState, replace) => {
        checkEmailExists(store, replace);
    };

    const checkResetOnEnter = ({ location: { query, pathname } }, replace) => {
        checkPasswordReset(store, query, replace, pathname);
    };

    return (
        <Route path={BASE_URL} component={PageWithInfo}>
            <IndexRoute
                component={ConnectedSignInScreen}
                onEnter={setUpFormOnEnter}
            />
            <Route
                path={CHECKOUT_PATH}
                component={ConnectedCheckoutSignInScreen}
                onEnter={setUpFormOnEnter}
            >
                <Route
                    path={LOG_IN_PATH}
                    component={ConnectedCheckoutSignInScreen}
                    onEnter={_checkAccountStatus}
                />
                <Route
                    path={SOCIAL_LOG_IN_PATH}
                    component={ConnectedCheckoutSignInScreen}
                />
            </Route>

            <Route
                path={CONFIRMATION_PATH}
                component={ConnectedConfirmationPage}
            />
            <Route
                path={LOG_IN_PATH}
                component={ConnectedSignInScreen}
                onEnter={_checkAccountStatus}
            />
            <Route
                path={SIGN_UP_PATH}
                component={ConnectedSignInScreen}
                onEnter={_checkAccountStatus}
            />
            <Route
                path={SOCIAL_SIGN_UP_PATH}
                component={ConnectedSignInScreen}
                onEnter={setUpSocialSingUpFormOnEnter}
            />
            <Route
                path={SOCIAL_LOOK_UP_PATH}
                component={ConnectedSocialLookupPage}
                onEnter={checkSocialToken}
            />
            <Route
                path={SOCIAL_LOG_IN_PATH}
                component={ConnectedSocialLoginPage}
                onEnter={_checkAccountStatus}
            />
            <Route
                path={SOCIAL_LINK_LOGIN_PATH}
                component={ConnectedSignInScreen}
                onEnter={setUpSocialLinkFormOnEnter}
            />
            <Route
                path={LINK_ACCOUNTS_LOG_IN_PATH}
                component={ConnectedSignInScreen}
                onEnter={_checkAccountStatus}
            />
            <Route
                path={SET_LINKED_SOFT_ACCOUNTS_PATH}
                component={ConnectedSignInScreen}
                onEnter={checkEmailOnEnter}
            />
            <Route
                path={RESET_PASSWORD_PATH}
                component={ConnectedSetPasswordPage}
                onEnter={checkResetOnEnter}
            />
            <Route
                path={SET_PASSWORD_PATH}
                component={ConnectedSetPasswordPage}
                onEnter={checkResetOnEnter}
            />
            <Route
                path={LINK_SOCIAL_ACCOUNT_PATH}
                component={ConnectedSocialLinkedAccountPage}
            />
            <Route path={STATUS_PATH}>
                <Route
                    path={SOFT_ACCOUNT_STATUS_PATH}
                    component={ConnectedStatusPage}
                />
                <Route
                    path={RESET_PASSWORD_STATUS_PATH}
                    component={ConnectedStatusPage}
                />
            </Route>
        </Route>
    );
};

export default getRoutes;
